import React, { useState, useCallback, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Stack, TextField, InputLabel, Typography, Divider } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { EventsCategoriesStoreContext } from "../../Store/EventCategoriesStore.ts";
import { DeleteForever, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import { addOrUpdateEventCategory, deleteCategory, getEventCategoryById } from "../../Requests/EventCategoriesService.ts";
import { EventCategories } from "../../Models/EventCategoriesModel.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView.jsx";


export default function EventCategoryInfo() {

  const navigate = useNavigate()
  const { eventcategoryid } = useParams()
  const store = useContext(EventsCategoriesStoreContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [name, setName] = useState<string | undefined>()
  const [createdAt, setCreatedAt] = useState<Date | undefined>()
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const APP_BAR_HEIGHT = 64
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const fetchData = useCallback(async () => {
    const result = await getEventCategoryById(eventcategoryid)

    setName(result.name)
    setCreatedAt(result.createdAt)
    setUpdatedAt(result.updatedAt)

    setIsLoading(false)
  }, [eventcategoryid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getEventCategoryForExport = () => ({
    objectId: eventcategoryid,
    name,
    createdAt,
    updatedAt,
  })

  const handleSaveChanges = async () => {
    let eventcategory = new EventCategories()
    eventcategory.objectId = eventcategoryid
    eventcategory.name = name
    await addOrUpdateEventCategory(eventcategory)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/eventcategories")
  }

  const handleDeleteCategory = async () => {
    await deleteCategory(eventcategoryid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/eventcategories")
  }

  const loaderView = <CircularProgressView />

  const contentView = <>
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "100%"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/eventcategories")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
    </Box>


    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Category name"}</InputLabel>
          <TextField
            placeholder="Category name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "sticky",
          top: HEADER_BOTTOM_OFFSET + 1,
          alignSelf: "flex-start",
          maxHeight: `calc(100vh - ${HEADER_BOTTOM_OFFSET + 1}px)`,
          overflowY: "auto"
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2,
            position: "sticky",
            top: 0,
            alignSelf: "flex-start"
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() => exportDataToCsv([getEventCategoryForExport()], eventcategoryid ? `eventcategory-${eventcategoryid}` : "eventcategory")}
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteCategory()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete Category
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
